import moment from 'moment';
import { mapObjIndexed, equals, cond, is, T, identity, either, anyPass, map, pick } from 'ramda';

import type { Item } from 'shared-types/Common';
import { formatPrice, getBookingValue } from '@features/analytics/utilities/format';

import type {
  TrackingOrder,
  SanitizedItem,
  SanitizedOrder,
  AnalyticsProduct,
  AnalyticsEcommerce,
} from './types';

type PropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | PropertyValue[]
  | { [key: string]: PropertyValue };

export const formatDatesAndPrices = mapObjIndexed((property: PropertyValue, key: string) =>
  cond<[PropertyValue], PropertyValue>([
    [
      () => either(equals('checkIn'), equals('checkOut'))(key),
      (p) => moment(p as string).format('DD-MM-YYYY'),
    ],
    [() => anyPass([equals('amount'), equals('revenue')])(key), (p) => (p as number) / 100],
    [is(Array), map(formatDatesAndPrices) as (p: PropertyValue) => PropertyValue],
    [is(Object), formatDatesAndPrices as unknown as (p: PropertyValue) => PropertyValue],
    [T, identity],
  ])(property)
);

const toMinorUnits = (amount: number): number => Math.round(amount * 100);

const sanitizeItem = (item: Item): SanitizedItem => ({
  id: item.id,
  type: item.type,
  price: item.price,
  quantity: item.quantity,
  checkIn: item.checkIn,
  checkOut: item.checkOut,
  adults: item.adults,
  children: item.children,
  ...(item.bookable && {
    bookable: pick(['id', 'ids', 'name', 'rate'], item.bookable),
  }),
  ...(item.product && {
    product: pick(['id', 'name'], item.product),
  }),
  ...(item.option && {
    option: pick(['name'], item.option),
  }),
  discount: item.discount ?? null,
});

export const sanitizeOrderForAnalytics = (order: TrackingOrder): SanitizedOrder => ({
  orderReference: order.orderReference,
  property: order.property ? pick(['name'], order.property) : undefined,
  total: order.total,
  ...(order.protectionFee && { protectionFee: order.protectionFee }),
  taxTotal: order.taxTotal,
  deliveryCost: order.deliveryCost,
  isDeferred: order.isDeferred,
  requiresPayment: order.requiresPayment,
  promoDetails: order.promoDetails,
  items: Array.isArray(order.items) ? order.items.map(sanitizeItem) : [],
});

const parseProduct = (product: SanitizedItem): AnalyticsProduct => ({
  quantity: product.quantity ?? 1,
  category: product.type,
  price: product.price.amount,
  ...(product.option && { variant: product.option.name }),
  ...(product.product && { name: product.product.name, id: product.product.id }),
  ...(product.bookable && {
    variant: product.bookable.rate,
    name: product.bookable.name,
    id: product.bookable.id,
  }),
  ...(product.type === 'rooms' && {
    quantity: moment(product.checkOut, 'DD-MM-YYYY').diff(
      moment(product.checkIn, 'DD-MM-YYYY'),
      'days'
    ),
  }),
  ...(product.type === 'tables' &&
    product.bookable && {
      variant: (product.bookable.ids as Record<string, string>)?.restaurant,
    }),
});

export const generateAnalyticsOrder = ({
  orderReference,
  taxTotal,
  deliveryCost,
  items,
  total,
  protectionFee,
  property,
}: SanitizedOrder): AnalyticsEcommerce => ({
  purchase: {
    actionField: {
      id: orderReference,
      revenue: total
        ? formatPrice(toMinorUnits(getBookingValue({ total, protectionFee })))
        : undefined,
      tax: taxTotal?.amount || 0,
      shipping: deliveryCost ? deliveryCost.amount : 0,
      brand: property?.name,
    },
    products: items.map((item) => parseProduct(item)),
  },
});
